import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../core/data/img/ImageWithBasePath";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { useStore } from "../../app/stores/store";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Form, FormControl, FormItem, FormLabel, FormMessage } from "../../components/ui/form";
import { observer } from "mobx-react-lite";
import { RegisterSchema, registerSchema } from "../../lib/schemas/registerSchema";
import { all_routes } from "../router/all_routes";
import { Loader2, Mail, User, Phone, Building2, ArrowLeft, Upload } from "lucide-react";
import Header from "../common/header";
import Footer from "../common/footer";

const routes = all_routes;

const PartnerRegister = () => {
  const navigate = useNavigate();
  const { userStore } = useStore();

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [tradePreview, setTradePreview] = useState<string | null>(null);
  const [emiratesIdPreview, setEmiratesIdPreview] = useState<string | null>(null);

  const form = useForm<RegisterSchema>({
    resolver: zodResolver(registerSchema),
    mode: "onTouched",
  });

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = form;

  const companyLogoFiles = watch("companyLogo");
  const tradeLicenseFiles = watch("tradeLicense");
  const emiratesIdFiles = watch("emiratesId");

  useEffect(() => {
    if (companyLogoFiles && companyLogoFiles.length > 0) {
      trigger("companyLogo");
    }
  }, [companyLogoFiles, trigger]);

  useEffect(() => {
    if (tradeLicenseFiles && tradeLicenseFiles.length > 0) {
      trigger("tradeLicense");
    }
  }, [tradeLicenseFiles, trigger]);

  useEffect(() => {
    if (emiratesIdFiles && emiratesIdFiles.length > 0) {
      trigger("emiratesId");
    }
  }, [emiratesIdFiles, trigger]);

  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      if (tradePreview) URL.revokeObjectURL(tradePreview);
      if (emiratesIdPreview) URL.revokeObjectURL(emiratesIdPreview);
    };
  }, [logoPreview, tradePreview, emiratesIdPreview]);

  const onSubmit = async (data: RegisterSchema) => {
    const formData = new FormData();

    formData.append("DisplayName", data.displayName);
    formData.append("Email", data.email);
    formData.append("Password", data.password);
    formData.append("PhoneNumber", data.phoneNumber);

    if (data.companyLogo?.length) {
      formData.append("CompanyLogo", data.companyLogo[0]);
    }

    if (data.tradeLicense?.length) {
      formData.append("TradeLicense", data.tradeLicense[0]);
    }

    if (data.emiratesId?.length) {
      formData.append("EmiratesId", data.emiratesId[0]);
    }

    const result = await userStore.partnerRegister(formData);

    if (result.status === "success") {
      toast.success(result.data || "Partner registered!");
      navigate("/login");
    } else {
      toast.error("Registration failed.");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />
      <main className="flex-grow pt-24 md:pt-32 py-8">
        <div className="custom-container mx-auto px-4 max-w-4xl">
          <Card className="border-0">
            <CardHeader className="text-center pb-4">
              <div className="flex justify-between items-center mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(routes.login)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
                <ImageWithBasePath
                  lazyLoad={true}
                  src="assets/img/new updated logo-01.png"
                  alt="Logo"
                  className="h-12"
                />
                <div className="w-12"></div>
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Partner Registration
              </CardTitle>
              <p className="text-gray-600 mt-2">
                Join our partner network and grow your business
              </p>
            </CardHeader>

            <CardContent>
              {/* Wrap the form with Form provider */}
              <Form {...form}>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                  encType="multipart/form-data"
                >
                  {/* First Name */}
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <div className="relative">
                      {/* <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /> */}
                      <Input
                        {...register("displayName")}
                        placeholder="Your first name"
                        className="pl-9 focus-visible:ring-[#e71875] focus-visible:border-[#e71875]"
                      />
                    </div>
                    <FormMessage className="text-red-500" />
                  </FormItem>

                  {/* Email */}
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <div className="relative">
                      {/* <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /> */}
                      <Input
                        type="email"
                        {...register("email")}
                        placeholder="you@company.com"
                        className="pl-9 focus-visible:ring-[#e71875] focus-visible:border-[#e71875]"
                      />
                    </div>
                    <FormMessage className="text-red-500" />
                  </FormItem>

                  {/* Password */}
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <Input
                      type="password"
                      {...register("password")}
                      placeholder="••••••••"
                      className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875]"
                    />
                    <FormMessage className="text-red-500" />
                  </FormItem>

                  {/* Phone */}
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <div className="relative">
                      {/* <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /> */}
                      <Input
                        {...register("phoneNumber")}
                        placeholder="Your phone number"
                        className="pl-9 focus-visible:ring-[#e71875] focus-visible:border-[#e71875]"
                      />
                    </div>
                    <FormMessage className="text-red-500" />
                  </FormItem>

                  {/* Company Logo */}
                  <FormItem className="md:col-span-2">
                    <FormLabel>Company Logo</FormLabel>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <Input
                        type="file"
                        accept="image/*"
                        {...register("companyLogo")}
                        className="cursor-pointer focus-visible:ring-[#e71875] focus-visible:border-[#e71875]"
                      />
                    </div>
                    <FormMessage className="text-red-500" />
                  </FormItem>

                  {/* Trade License */}
                  <FormItem>
                    <FormLabel>Trade License</FormLabel>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        {...register("tradeLicense")}
                        className="cursor-pointer focus-visible:ring-[#e71875] focus-visible:border-[#e71875]"
                      />
                    </div>
                    <FormMessage className="text-red-500" />
                  </FormItem>

                  {/* Emirates ID */}
                  <FormItem>
                    <FormLabel>Emirates ID</FormLabel>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        {...register("emiratesId")}
                        className="cursor-pointer focus-visible:ring-[#e71875] focus-visible:border-[#e71875]"
                      />
                    </div>
                    <FormMessage className="text-red-500" />
                  </FormItem>

                  {/* Submit Button */}
                  <div className="md:col-span-2">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary hover:bg-primary/90 text-white py-6 text-lg"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Registering...
                        </>
                      ) : (
                        "Register as Partner"
                      )}
                    </Button>
                  </div>
                </form>
              </Form>

              <div className="text-center pt-6 border-t border-gray-200 mt-6">
                <p className="text-sm text-gray-600">
                  Already have an account?{" "}
                  <Link
                    to={routes.login}
                    className="text-primary hover:text-primary/80 font-medium"
                  >
                    Sign In
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default observer(PartnerRegister);